<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import Games from "@/services/Games";
import modalAddDownload from "@/components/modals/games/modalAddDownload.vue";
import Swal from "sweetalert2";

export default {
  components: { Layout, PageHeader, modalAddDownload },
  page: {
    title: "Downloads",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  data() {
    return {
      title: "Downloads",
      items: [
        { text: "Fanzone" },
        { text: "Downloads", active: true },
      ],
      error: null,
      tableData: [],
      isBusy: false,
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      statusFilter: "all", // all | active | inactive
      filter: null,
      filterOn: ["name", "category_name"],
      sortBy: "name",
      sortDesc: false,
      lightboxVisible: false,
      selectedImage: "",
      fields: [
        { key: "id", label: "ID", sortable: true },
        { key: "name", label: "Name", sortable: true },
        { key: "category_name", label: "Category", sortable: true },
        { key: "file_path", label: "Image", sortable: false },
        { key: "active", label: "Active", sortable: true },
        { key: "actions", label: "Actions", sortable: false },
      ],
    };
  },
  computed: {
    rows() {
      return this.tableData.length;
    },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.tableData.slice(start, end);
    },
    filteredItems() {
      if (!this.filter) return this.tableData;
      
      return this.tableData.filter(item => {
        return Object.keys(item).some(key => {
          if (!this.filterOn.includes(key)) return false;
          return String(item[key]).toLowerCase().includes(this.filter.toLowerCase());
        });
      });
    }
  },
  mounted() {
    this.fetchDownloads();
  },
  methods: {
    modalAddDownload() {
      this.$bvModal.show("add_download");
    },
    async fetchDownloads() {
      try {
        this.toggleBusy();
        const params = {};
        if (this.statusFilter === "all") {
          params.include_inactive = 1;
        } else if (this.statusFilter === "inactive") {
          params.active = 0;
          params.include_inactive = 1;
        } else {
          // active only (default behavior of endpoint)
          params.active = 1;
        }

        const response = await Games.getDownloads(params);
        this.tableData = response.data.data || [];
        this.totalRows = this.tableData.length;
      } catch (error) {
        this.tableData = [];
        this.error =
          (error.response && error.response.data && error.response.data.error) ||
          "Failed to load downloads";
      } finally {
        this.toggleBusy();
      }
    },
    toggleBusy() {
      this.isBusy = !this.isBusy;
    },
    async toggleActive(item) {
      try {
        this.toggleBusy();
        const newStatus = item.active ? 0 : 1;
        await Games.updateDownloadStatus(item.id, newStatus);
        item.active = newStatus;
        
        this.$bvToast.toast("Status updated successfully", {
          title: "Success",
          variant: "success",
          solid: true,
        });
      } catch (error) {
        console.error("Failed to toggle active", error);
        this.$bvToast.toast("Failed to update status", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.toggleBusy();
      }
    },
    openLightbox(item) {
      this.selectedImage = item.file_path;
      this.lightboxVisible = true;
    },
    formatBytes(bytes) {
      const b = Number(bytes);
      if (!b || Number.isNaN(b)) return null;
      const mb = b / (1024 * 1024);
      if (mb >= 1) return `${mb.toFixed(1)} MB`;
      const kb = b / 1024;
      return `${kb.toFixed(0)} KB`;
    },
    orientation(item) {
      const w = Number(item.width);
      const h = Number(item.height);
      if (!w || !h || Number.isNaN(w) || Number.isNaN(h)) return null;
      if (h > w) return "Portrait";
      if (w > h) return "Landscape";
      return "Square";
    },

    async deleteItem(item) {
      const result = await Swal.fire({
        title: "Confirm Delete",
        text: `Are you sure you want to delete "${item.name}"?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#dc3545",
      });

      if (!result.isConfirmed) return;

      try {
        await Games.deleteDownload(item.id);
        await this.fetchDownloads();
        await Swal.fire("Deleted!", "Wallpaper has been deleted.", "success");
      } catch (error) {
        const msg =
          (error.response && error.response.data && error.response.data.error) ||
          "Failed to delete wallpaper";
        await Swal.fire("Error", msg, "error");
      }
    }
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <!-- Controls -->
            <div class="row mb-4 align-items-center">
              <div class="col-md-6 d-flex align-items-center gap-3 flex-wrap">
                <div class="status-segment" role="group" aria-label="Status filter">
                  <button
                    type="button"
                    class="status-segment__btn"
                    :class="{ active: statusFilter === 'all' }"
                    @click="statusFilter = 'all'; fetchDownloads();"
                  >
                    All
                  </button>
                  <button
                    type="button"
                    class="status-segment__btn"
                    :class="{ active: statusFilter === 'active' }"
                    @click="statusFilter = 'active'; fetchDownloads();"
                  >
                    Active
                  </button>
                  <button
                    type="button"
                    class="status-segment__btn"
                    :class="{ active: statusFilter === 'inactive' }"
                    @click="statusFilter = 'inactive'; fetchDownloads();"
                  >
                    Inactive
                  </button>
                </div>
              </div>

              <div class="col-md-6 text-md-end">
                <a
                  href="javascript:void(0);"
                  class="btn btn-success waves-effect waves-light"
                  @click="modalAddDownload"
                >
                  <i class="mdi mdi-plus me-2"></i>
                  Add Download
                </a>
              </div>
            </div>

            <!-- Loading Spinner -->
            <div v-if="isBusy" class="text-center py-5">
              <b-spinner variant="primary" label="Loading..."></b-spinner>
              <p class="text-muted mt-2">Loading downloads...</p>
            </div>

            <!-- Grid View -->
            <div v-else-if="filteredItems.length" class="row g-3 mt-2">
              <div 
                v-for="item in paginatedItems" 
                :key="item.id" 
                class="col-xl-3 col-lg-4 col-md-6 col-sm-6"
              >
                <div class="card h-100 shadow-sm hover-card">
                  <!-- Image Container -->
                  <div class="position-relative">
                    <img 
                      v-if="item.file_path"
                      :src="item.file_path" 
                      class="card-img-top grid-image"
                      :alt="item.name"
                      @click="openLightbox(item)"
                    />
                    <div v-else class="card-img-top grid-image bg-light d-flex align-items-center justify-content-center">
                      <i class="mdi mdi-image-off text-muted" style="font-size: 48px;"></i>
                    </div>
                    
                    <!-- Status Badge -->
                    <span 
                      class="position-absolute top-0 end-0 m-2 badge rounded-pill"
                      :class="item.active ? 'bg-success' : 'bg-danger'"
                    >
                      {{ item.active ? 'Active' : 'Inactive' }}
                    </span> 
                    <!-- Hover Actions -->
                    <div class="image-hover-actions">
                      <button class="btn btn-sm btn-light me-1" @click="openLightbox(item)">
                        <i class="mdi mdi-magnify"></i>
                      </button>
                      <button class="btn btn-sm btn-light" @click="deleteItem(item)">
                        <i class="mdi mdi-delete"></i>
                      </button>
                    </div>
                  </div>
                  
                  <!-- Card Body -->
                  <div class="card-body d-flex justify-content-between align-items-start">
                    <div class="me-2 flex-grow-1 overflow-hidden">
                      <h6 class="card-title text-truncate mb-1" :title="item.name">
                        {{ item.name }}
                      </h6>

                      <div class="text-muted small d-flex flex-wrap gap-2">
                        <span v-if="orientation(item)" class="badge bg-light text-dark border">
                          {{ orientation(item) }}
                        </span>
                        <span v-if="item.width && item.height" class="badge bg-light text-dark border">
                          {{ item.width }}×{{ item.height }}
                        </span>
                        <span v-if="formatBytes(item.size_bytes)" class="badge bg-light text-dark border">
                          {{ formatBytes(item.size_bytes) }}
                        </span>
                        <span v-if="item.mime" class="badge bg-light text-dark border">
                          {{ item.mime }}
                        </span>
                      </div>
                    </div>

                    <!-- Status Toggle -->
                    <div class="form-check form-switch mt-1">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        role="switch"
                        :checked="Number(item.active) === 1"
                        @change="toggleActive(item)"
                        :id="'grid-switch-' + item.id"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else class="text-center py-5">
              <i class="mdi mdi-image-off" style="font-size: 64px; color: #ccc;"></i>
              <h5 class="mt-3 text-muted">No downloads found</h5>
              <p class="text-muted">Click "Add Download" to create your first download item.</p>
            </div>

            <!-- Pagination -->
            <div v-if="filteredItems.length" class="row mt-4">
              <div class="col-sm-12 col-md-5">
                <div class="dataTables_info">
                  Showing {{ (currentPage - 1) * perPage + 1 }}
                  to {{ Math.min(currentPage * perPage, filteredItems.length) }}
                  of {{ filteredItems.length }} entries
                </div>
              </div>
              <div class="col-sm-12 col-md-7">
                <div class="dataTables_paginate paging_simple_numbers float-end">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination
                      v-model="currentPage"
                      :total-rows="filteredItems.length"
                      :per-page="perPage"
                    ></b-pagination>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Error Alert -->
            <b-alert v-if="error" variant="danger" show class="mt-3 mb-0">
              <i class="mdi mdi-alert-circle me-2"></i>
              {{ error }}
            </b-alert>
          </div>
        </div>
      </div>
    </div>

    <!-- Lightbox Modal -->
    <b-modal 
      v-model="lightboxVisible" 
      size="lg" 
      title="Image Preview"
      hide-footer
      centered
      body-class="p-0"
    >
      <div class="text-center p-3">
        <img 
          :src="selectedImage" 
          class="img-fluid rounded"
          style="max-height: 70vh; width: auto;"
        />
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="lightboxVisible = false">Close</button>
      </div>
    </b-modal>

    <!-- Add Download Modal -->
    <modal-add-download @onRefresh="fetchDownloads" />
  </Layout>
</template>

<style scoped>
/* Grid View Styles */
.grid-image {
  width: 100%;
  height: auto;
  max-height: 500px;
  object-fit: contain;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.hover-card {
  transition: all 0.3s ease;
  overflow: hidden;
}

.hover-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
}

.hover-card:hover .grid-image {
  transform: scale(1.05);
}

.image-hover-actions {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0;
  transition: opacity 0.3s ease;
  display: flex;
  gap: 5px;
  z-index: 10;
}

.hover-card:hover .image-hover-actions {
  opacity: 1;
}

.image-hover-actions .btn {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
}

.image-hover-actions .btn:hover {
  transform: scale(1.1);
}

/* Table View Styles */
.preview-thumbnail {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.2s ease;
  border: 2px solid #f0f0f0;
}

.preview-thumbnail:hover {
  transform: scale(1.5);
  box-shadow: 0 4px 8px rgba(0,0,0,0.2);
  z-index: 100;
  position: relative;
}

.image-preview-container {
  position: relative;
  display: inline-block;
}

/* Status segmented control */
.status-segment {
  display: inline-flex;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 999px;
  overflow: hidden;
  background: #fff;
}

.status-segment__btn {
  border: 0;
  background: transparent;
  padding: 6px 12px;
  font-size: 0.85rem;
  line-height: 1;
  color: #495057;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.status-segment__btn:hover {
  background: rgba(0, 0, 0, 0.04);
}

.status-segment__btn.active {
  background: #556ee6; /* matches template primary */
  color: #fff;
}

.status-segment__btn + .status-segment__btn {
  border-left: 1px solid rgba(0, 0, 0, 0.08);
}

/* Toggle Switch */
.form-switch .form-check-input {
  width: 45px;
  height: 22px;
  cursor: pointer;
}

/* Card Body */
.card-body {
  padding: 0.75rem;
}

.card-title {
  font-size: 0.95rem;
  font-weight: 600;
}

/* Badge Styles */
.badge {
  font-size: 0.7rem;
  font-weight: 500;
}

/* Pagination */
.pagination {
  margin: 0;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .grid-image {
    height: 150px;
  }
  
  .preview-thumbnail:hover {
    transform: scale(1.2);
  }
}

/* Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.card {
  animation: fadeIn 0.3s ease;
}

/* Status Colors */
.bg-success {
  background-color: #28a745 !important;
}

.bg-danger {
  background-color: #dc3545 !important;
}

.bg-info {
  background-color: #17a2b8 !important;
}

/* Hover Effects */
.btn-outline-primary:hover,
.btn-outline-danger:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

/* Loading State */
.text-center.py-5 {
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>